import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import {
  getTranslatedText,
  SkeletonLoading,
  getDateOperators,
  getDefaultValue,
  updateConditions,
  TableContainer
} from '@nile/components';
import { RMMClient } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import {
  GET_SOFTWARELIST_DATATABLE,
  GET_SOFTWARE_META
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import {
  columnsConfig,
  getAttributeName,
  getColumnAccessor,
  getColumnWidth,
  getAssetClassVariables,
  getColumnCellType
} from '@nile/rmm/src/modules/asset-management/services/asset-list-helper';
import { getProcessedConfig } from '@nile/rmm/src/modules/common/services/tableHelper';
import { isEmpty } from 'lodash';
import React, { useEffect, useState, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import RMMContext from '@nile/rmm/src/modules/common/contexts/RMMContext';
import { useIntl } from 'react-intl';

const defaultOptions = {
  page: 1,
  pageSize: 50,
  hasMore: null,
  totalCount: 0,
  sort: [
    {
      attribute: 'workStation.assetId',
      order: 'ASC'
    }
  ]
};

const AssetSoftwareList = (props) => {
  const { selectedMetric, entitiesMeta, defaultCondition, module } = props;

  const intl = useIntl();
  const [totalCount, setTotalCount] = useState(0);
  const [processedAllColumns, setProcessedAllColumns] = useState([]);
  const [softwareList, setSoftwareList] = useState([]);
  const [listInfo, setListInfo] = useState(defaultOptions);

  const [allColumns, setAllColumns] = useState([]);
  const [sortAttribute, setSortAttribute] = useState([]);
  const [tableLoader, setTableLoader] = useState(false);

  // Bulk action context functions
  const rmmContext = useContext(RMMContext);
  const [records, setRecords] = useState([]);

  const pageSize = 25;
  const [listLoader, setlistLoader] = useState(false);
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);

  const fetchSoftwareMetaInfo = () => {
    RMMClient.query({
      query: GET_SOFTWARE_META
    }).then(({ data }) => {
      const {
        selectAttributes,
        sortAttributes
      } = data?.getAssetSoftwareInventoryMetaInfo;
      setAllColumns(selectAttributes);
      setSortAttribute(sortAttributes);
    });
  };

  function setViewConditions(data) {
    setListInfo({ ...listInfo, condition: data });
  }

  function updateAllConditions(condition, selectedField) {
    updateConditions({
      condition,
      selectedField,
      viewConditions: listInfo?.condition || null,
      setConditions: setViewConditions
    });
  }

  const getColumnName = (colu) => {
    if (colu?.label) {
      return getTranslatedText(intl, colu?.label.toLowerCase());
    }
    switch (colu?.name) {
      case 'workStation.client.name':
        return getTranslatedText(intl, 'attribute.clientName');
      case 'workStation.site.name':
        return getTranslatedText(intl, 'attribute.siteName');
      default:
        return colu?.name;
    }
  };

  const constructTableData = (cols) => {
    let columns = [];

    cols.map((col) => {
      columns.push({
        Header: getColumnName(col),
        minWidth: getColumnWidth(col.name, true),
        width: getColumnWidth(col.name),
        accessor: getColumnAccessor(col),
        type: getColumnCellType(col),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableSortBy: false,
        editable: false,
        disableFilters: true,
        field: {
          ...col,
          label: getColumnName(col)
        },
        helpers: {
          getDefaultValue,
          updateConditions: updateAllConditions,
          getDateOperators,
          getVariables: getAssetClassVariables.bind(this, col)
        }
      });
    });

    setColumnGroup([{ Header: 'Name', columns }]);
  };

  const fetchSoftwareList = (data) => {
    const { condition } = defaultOptions;
    setlistLoader(true);

    const { configuredCols } = getProcessedConfig({
      entitiesMeta,
      allColumns,
      intl,
      module
    });

    RMMClient.query({
      query: GET_SOFTWARELIST_DATATABLE,
      fetchPolicy: 'network-only',
      variables: {
        metricId: selectedMetric?.metricId,
        listInfo: {
          condition:
            !isEmpty(data?.condition) ||
            !isEmpty(condition) ||
            !isEmpty(defaultCondition)
              ? { ...data?.condition, ...condition, ...defaultCondition }
              : null,
          page: data?.page || 1,
          pageSize,
          sort: data?.sort
        }
      }
    }).then((res) => {
      if (res) {
        constructTableData(configuredCols, res);
        setSoftwareList(res?.data?.getSoftwareInventoryDataTable?.assets);
        setTotalCount(
          res?.data?.getSoftwareInventoryDataTable?.listInfo?.totalCount || 0
        );
        setListInfo(res?.data?.getSoftwareInventoryDataTable?.listInfo);
        setlistLoader(false);
        setTableLoader(false);
      }
    });
  };

  function tableChange(context, data) {
    if (data) {
      setListInfo({ ...listInfo, ...data });
      fetchSoftwareList({ ...listInfo, ...data });
    }
  }

  const context = {
    setRecords: (data) => {
      setRecords(data);
      if (rmmContext !== 'rmm') {
        rmmContext.setBulkRecords(data);
      }
    },
    records,
    viewConditions: listInfo?.condition
  };

  useEffect(() => {
    if (!isEmpty(processedAllColumns)) {
      fetchSoftwareList({ ...defaultOptions, ...listInfo });
    }
  }, [processedAllColumns]);

  useEffect(() => {
    if (allColumns.length > 0 && !isEmpty(entitiesMeta)) {
      const { configuredCols } = getProcessedConfig({
        entitiesMeta,
        allColumns,
        intl,
        module
      });
      setProcessedAllColumns(configuredCols);
    }
  }, [allColumns, entitiesMeta]);

  useEffect(() => {
    if (allColumns.length === 0) {
      fetchSoftwareMetaInfo();
    }
    setAllColumns([]);
    setProcessedAllColumns([]);
    setTableLoader(true);
  }, []);

  if (tableLoader || processedAllColumns.length === 0) {
    return <SkeletonLoading hideStepNav hideSecondaryHeader hideHeader />;
  }
  return (
    <TableContainer className="table-container">
      <TableView
        cardTable
        fullHeight
        isLoading={listLoader || processedAllColumns.length === 0}
        context={context}
        columns={columnGroup}
        listInfo={listInfo}
        defaultColumnCells={columnsConfig}
        data={softwareList || []}
        rowCount={pageSize}
        refreshTable={tableChange}
        noBulkSelect
        getAttributeName={getAttributeName}
        skipPageReset={false}
        pagination={Math.ceil(totalCount / pageSize) > 1}
        pages={Math.ceil(totalCount / pageSize) || 0}
        activePage={listInfo?.page}
        recordCount={totalCount}
        emptyState={{
          nodataImage: `${envConfig.imageRoot}svg/alert-empty-state.svg`,
          heading: getTranslatedText(intl, 'widget.noSoftwareFound.heading')
        }}
      />
    </TableContainer>
  );
};

export default withRouter(AssetSoftwareList);
